import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { ensureTaxInfo } from '../../util/taxInfoMigration';
import { isYtunnusPlain } from '../../util/taxInfoSchema';
import { vatStatusReminder } from '../../util/vat';
import { fetchCurrentUser } from '../../ducks/user.duck';
import {
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperAccountSettingsSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  Page,
} from '../../components';
import { TaxInformationForm } from '../../forms';
import { TopbarContainer } from '../../containers';

import { isScrollingDisabled } from '../../ducks/UI.duck';
import { saveTaxInformation, saveContactDetailsClear } from './TaxInformationPage.duck';
import css from './TaxInformationPage.css';

export const taxToInitialValues = (
  tax,
  profile,
  protectedData = {},
  accountRole = null,
  isCompanyAbroad = false
) => {
  const country =
    tax?.address?.country ||
    (tax?.residency?.outsideEu ? '' : tax?.residency?.countryCodes?.[0] || 'FI');
  const identifier = tax?.identifier;
  const isFi = country === 'FI';
  // Legacy Finnish records stored a "VAT number" under protectedData.vat_number,
  // but historically users entered it in many shapes (ALV-tunnus "FI12345678",
  // bare "12345678", actual Y-tunnus "1234567-8", …). Only treat it as a
  // Y-tunnus prefill when it already matches the expected format — otherwise
  // it would silently fail ytunnusValid and lock the save button.
  const legacyYtunnus =
    isFi && isYtunnusPlain(protectedData?.vat_number) ? protectedData.vat_number : '';
  // Only pre-select the classification radios when the user has actually
  // confirmed via the new form (schemaVersion >= 1). For migrated legacy
  // records (schemaVersion === 0), the form's best guess from `account_role`
  // can be wrong (e.g. light-entrepreneur isn't always toiminimi), so we leave
  // the radio empty and force the user to pick.
  const confirmed = tax?.schemaVersion >= 1;
  const sellerTypeInitial =
    confirmed && !isFi
      ? tax?.name?.form === 'ENTITY'
        ? 'BUSINESS'
        : tax?.name?.form === 'INDIVIDUAL'
        ? 'INDIVIDUAL'
        : null
      : null;
  const finCompanyFormInitial =
    confirmed && isFi
      ? tax?.name?.form === 'ENTITY'
        ? 'ENTITY'
        : tax?.name?.form === 'INDIVIDUAL'
        ? 'INDIVIDUAL_NO_BUSINESS_ID'
        : null
      : null;
  // FI individuals can have a Y-tunnus stashed on businessRegistration.
  // FI entities have the Y-tunnus directly on identifier. Migrated legacy
  // records can hold arbitrary strings on businessRegistration.number, so it
  // gets the same format guard as vat_number above.
  const businessRegYtunnus = isYtunnusPlain(tax?.businessRegistration?.number)
    ? tax.businessRegistration.number
    : '';
  const ytunnusInitial = isFi
    ? identifier?.type === 'YTUNNUS'
      ? identifier.plaintext || legacyYtunnus
      : businessRegYtunnus || legacyYtunnus
    : '';
  // For confirmed records trust the saved businessRegistration — an INDIVIDUAL
  // who explicitly opted out of providing a Y-tunnus shouldn't have the toggle
  // re-checked on next visit from a lingering legacy vat_number. The legacy
  // hint only applies to unconfirmed (schemaVersion < 1) records.
  const finHasYtunnusInitial =
    isFi &&
    tax?.name?.form === 'INDIVIDUAL' &&
    (!!tax?.businessRegistration?.number || (!confirmed && !!legacyYtunnus));

  return {
    country,
    finCompanyForm: finCompanyFormInitial,
    finHasYtunnus: finHasYtunnusInitial,
    sellerType: sellerTypeInitial,
    ytunnus: ytunnusInitial,
    hetu: '', // never pre-fill; user must re-enter to change
    tin: identifier?.type === 'OTHER_TIN' ? identifier.plaintext || '' : '',
    tinCountry: identifier?.type === 'OTHER_TIN' ? identifier.issuedBy || '' : '',
    noTin: false,
    legalName: tax?.name?.legalName || '',
    firstName: tax?.name?.firstName || profile?.firstName || '',
    lastName: tax?.name?.lastName || profile?.lastName || '',
    vatStatus:
      tax?.vatStatus ||
      (accountRole === 'light-entrepreneur' && !isCompanyAbroad ? 'INVOICING_SERVICE' : null),
    vatNumber: tax?.vatNumber || '',
    exemptId: tax?.exemptId || '',
    homeVatRate: tax?.homeVat ? String(Math.round(tax.homeVat.rate * 10000) / 100) : '0',
    homeVatLabel: tax?.homeVat?.label || 'VAT',
    invoicingServiceName: tax?.invoicingService?.name || '',
    invoicingServiceVatNumber: tax?.invoicingService?.vatNumber || '',
    companyForm: tax?.companyForm || '',
    businessRegNumber: !isFi ? tax?.businessRegistration?.number || '' : '',
    businessRegCountry: !isFi ? tax?.businessRegistration?.countryOfIssue || '' : '',
    iban: tax?.iban || '',
    street: tax?.address?.street || '',
    postcode: tax?.address?.postcode || '',
    city: tax?.address?.city || '',
    birthCity: tax?.birthInfo?.city || '',
    birthCountry: tax?.birthInfo?.country || '',
    dateOfBirth: tax?.birthInfo?.date
      ? (() => {
          const m = String(tax.birthInfo.date).match(/^(\d{4})-(\d{2})-(\d{2})/);
          return m ? { year: m[1], month: m[2], day: m[3] } : null;
        })()
      : null,
    submittedAt: tax?.submittedAt || null,
  };
};

export const TaxInformationPageComponent = props => {
  const {
    currentUser,
    onChange,
    scrollingDisabled,
    onTaxInformationSubmit,
    intl,
    saveTaxInformationInProgress,
    taxInformationChanged,
    taxInformationSuccess,
    taxInformationError,
    lastSavedIdentifier,
    lastSavedVatValidationStatus,
  } = props;

  // Save success/error flags persist in Redux across navigation — clear
  // stale results from a previous visit when the page is (re)entered.
  useEffect(() => {
    onChange();
  }, [onChange]);

  const user = ensureCurrentUser(currentUser);
  const profile = user.attributes.profile || {};
  const privateData = profile.privateData || {};
  const protectedData = profile.protectedData || {};

  const tax = user.id ? ensureTaxInfo(privateData, protectedData, profile) : null;

  const initialMaskedIdentifier =
    tax?.identifier && (tax.identifier.type === 'HETU' || tax.identifier.type === 'OTHER_TIN')
      ? { last4: tax.identifier.last4, type: tax.identifier.type }
      : lastSavedIdentifier;

  const contactInfoForm = user.id ? (
    <TaxInformationForm
      className={css.form}
      initialValues={taxToInitialValues(
        tax,
        profile,
        protectedData,
        profile.publicData?.account_role,
        !!privateData.isCompanyAbroad
      )}
      currentUser={currentUser}
      initialMaskedIdentifier={initialMaskedIdentifier}
      onSubmit={values => onTaxInformationSubmit(values)}
      onChange={onChange}
      inProgress={saveTaxInformationInProgress}
      taxInformationChanged={taxInformationChanged}
      taxInformationSuccess={taxInformationSuccess}
      taxInformationError={taxInformationError}
      vatValidationStatus={lastSavedVatValidationStatus}
      vatStatusNeedsConfirmation={!!vatStatusReminder(tax)}
    />
  ) : null;

  const title = intl.formatMessage({ id: 'TaxInformationPage.title' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="TaxInformationPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperAccountSettingsSideNav
          currentTab="TaxInformationPage"
          currentUser={currentUser}
        />
        <LayoutWrapperMain>
          <div className={css.content}>
            <h1 className={css.title}>
              <FormattedMessage id="TaxInformationPage.heading" />
            </h1>
            {contactInfoForm}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

TaxInformationPageComponent.defaultProps = {
  currentUser: null,
  lastSavedIdentifier: null,
  lastSavedVatValidationStatus: null,
};

const { bool, func, object, string } = PropTypes;

TaxInformationPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  onChange: func.isRequired,
  onTaxInformationSubmit: func.isRequired,
  scrollingDisabled: bool.isRequired,
  lastSavedIdentifier: object,
  lastSavedVatValidationStatus: string,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    saveTaxInformationInProgress,
    taxInformationError,
    taxInformationSuccess,
    taxInformationChanged,
    lastSavedIdentifier,
    lastSavedVatValidationStatus,
  } = state.TaxInformationPage;
  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    saveTaxInformationInProgress,
    taxInformationError,
    taxInformationSuccess,
    taxInformationChanged,
    lastSavedIdentifier,
    lastSavedVatValidationStatus,
  };
};

const mapDispatchToProps = dispatch => ({
  onChange: () => dispatch(saveContactDetailsClear()),
  onTaxInformationSubmit: values => dispatch(saveTaxInformation(values)),
});

const TaxInformationPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(TaxInformationPageComponent);

TaxInformationPage.loadData = () => {
  // Since verify email happens in separate tab, current user's data might be updated
  return fetchCurrentUser();
};

export default TaxInformationPage;
